// DataCard — primitiva lista condivisa (Card-pivot mobile).
// Skin ereditata dai design token (--radius-card, --ds-*, --chip, --fs-card-*,
// --space-*). Usata dalle liste CRUD in vista mobile (<768px) al posto della
// <table> desktop, che resta invariata. Contratto:
//   title     campo primario (string | node)
//   subtitle  secondario opzionale
//   badge     node opzionale (es. pill categoria / <LifecycleBadge/>)
//   meta      [{ label, value }] righe chiave-valore (value string | node)
//   actions   node opzionale (bottoni) — resi in regione separata dalla tap-area
//   onClick   opzionale: tap → dettaglio/modal
//
// A11y: mai innestare bottoni dentro un role="button". Se ci sono `actions`,
// la tap-area (titolo+meta) è un role="button" a sé e le azioni vivono in una
// regione sorella; senza `actions` è l'intera card a essere il bottone.

const cardBase = {
  background: 'var(--ds-bg)',
  border: '1px solid var(--ds-border)',
  borderRadius: 'var(--radius-card)',
  padding: 'var(--space-4)',
  boxShadow: '0 1px 2px rgba(35,38,47,0.06)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
};

export default function DataCard({ title, subtitle, badge, meta = [], actions, onClick }) {
  const clickable = typeof onClick === 'function';
  const hasActions = !!actions;
  const tapOnCard = clickable && !hasActions;   // niente azioni → tap = intera card
  const tapOnBody = clickable && hasActions;    // con azioni → tap = solo il corpo
  const metaRows = meta.filter(Boolean);

  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
  };

  const content = (
    <>
      {/* Header: titolo + badge */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-card-title)', fontWeight: 600, color: 'var(--ink)', wordBreak: 'break-word' }}>{title}</div>
          {subtitle != null && subtitle !== '' && (
            <div style={{ fontSize: 'var(--fs-card-meta)', color: 'var(--ds-muted)', marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
        {badge != null && <div style={{ flexShrink: 0 }}>{badge}</div>}
      </div>

      {/* Meta chiave-valore */}
      {metaRows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', marginTop: 'var(--space-1)' }}>
          {metaRows.map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
              <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{m.label}</span>
              <span style={{ fontSize: 'var(--fs-card-meta)', fontWeight: 500, color: 'var(--ink)', textAlign: 'right' }}>{m.value}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );

  // Hover "lift" quando la card è cliccabile (coerente con HubCard). Un box non
  // cliccabile non si solleva (niente falso invito al click).
  const lift = clickable ? (e) => {
    e.currentTarget.style.boxShadow = '0 2px 6px rgba(31,42,40,.07),0 18px 42px rgba(31,42,40,.10)';
    e.currentTarget.style.borderColor = 'var(--border-2, var(--ds-border))';
    e.currentTarget.style.transform = 'translateY(-2px)';
  } : undefined;
  const rest = clickable ? (e) => {
    e.currentTarget.style.boxShadow = '0 1px 2px rgba(35,38,47,0.06)';
    e.currentTarget.style.borderColor = 'var(--ds-border)';
    e.currentTarget.style.transform = 'none';
  } : undefined;

  return (
    <div
      style={{ ...cardBase, cursor: tapOnCard ? 'pointer' : 'default', transition: 'box-shadow .18s, border-color .18s, transform .18s' }}
      onClick={tapOnCard ? onClick : undefined}
      onKeyDown={tapOnCard ? onKey : undefined}
      role={tapOnCard ? 'button' : undefined}
      tabIndex={tapOnCard ? 0 : undefined}
      onMouseEnter={lift}
      onMouseLeave={rest}
    >
      {tapOnBody ? (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', cursor: 'pointer' }}
          onClick={onClick}
          onKeyDown={onKey}
          role="button"
          tabIndex={0}
        >
          {content}
        </div>
      ) : content}

      {hasActions && (
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 'var(--space-1)' }}>
          {actions}
        </div>
      )}
    </div>
  );
}
